import React, { useEffect, useState } from 'react';
import FlashcardSetList from './components/FlashcardSetList';
import Flashcard from './components/Flashcard';

export default function App() {
  const [sets, setSets] = useState([]);
  const [selectedSet, setSelectedSet] = useState(null);
  const [cards, setCards] = useState([]);
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [shuffle, setShuffle] = useState(false);
  const [autoFlip, setAutoFlip] = useState(false);
  const [flipInterval, setFlipInterval] = useState(3000);
  const [darkMode, setDarkMode] = useState(false);

  // Fetch flashcard sets
  useEffect(() => {
    fetch('/api/get_sets.php')
      .then((res) => res.json())
      .then((data) => setSets(data));
  }, []);

  // Fetch cards for selected set
  useEffect(() => {
    if (!selectedSet) return;
    fetch(`/api/get_cards.php?set_id=${selectedSet.id}`)
      .then((res) => res.json())
      .then((data) => {
        setCards(data);
        setIndex(0);
        setFlipped(false);
      });
  }, [selectedSet]);

  // Auto-flip effect
  useEffect(() => {
    if (!autoFlip || cards.length === 0) return;

    const timer = setInterval(() => {
      setFlipped((prev) => {
        if (prev) {
          nextCard(); // advance after showing back
          return false;
        }
        return true; // flip to back
      });
    }, flipInterval);

    return () => clearInterval(timer);
  }, [autoFlip, flipInterval, cards, index, shuffle]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowRight':
          nextCard();
          break;
        case 'ArrowLeft':
          if (!shuffle) prevCard();
          break;
        case ' ':
          e.preventDefault();
          setFlipped((prev) => !prev);
          break;
        case 'r':
        case 'R':
          restartSet();
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [index, shuffle, cards]);

  // Navigation functions
  const nextCard = () => {
    if (cards.length === 0) return;

    if (shuffle) {
      let nextIndex;
      do {
        nextIndex = Math.floor(Math.random() * cards.length);
      } while (nextIndex === index && cards.length > 1);
      setIndex(nextIndex);
    } else {
      setIndex((prev) => (prev + 1) % cards.length);
    }
    setFlipped(false);
  };

  const prevCard = () => {
    if (!shuffle) {
      setIndex((prev) => (prev - 1 + cards.length) % cards.length);
      setFlipped(false);
    }
  };

  const restartSet = () => {
    if (shuffle && cards.length > 0) {
      const randomIndex = Math.floor(Math.random() * cards.length);
      setIndex(randomIndex);
    } else {
      setIndex(0);
    }
    setFlipped(false);
  };

  if (!selectedSet) {
    return (
      <div
        style={{
          padding: '20px',
          backgroundColor: darkMode ? '#121212' : '#f5f5f5',
          color: darkMode ? '#f5f5f5' : '#000',
          minHeight: '100vh'
        }}
      >
        <label style={{ display: 'block', marginBottom: '10px' }}>
          <input
            type="checkbox"
            checked={darkMode}
            onChange={(e) => setDarkMode(e.target.checked)}
            style={{ marginRight: '5px' }}
          />
          Dark Mode
        </label>

        <h1>Choose a Flashcard Set</h1>
        <FlashcardSetList sets={sets} onSelectSet={setSelectedSet} />
      </div>
    );
  }

  return (
    <div
      style={{
        padding: '20px',
        backgroundColor: darkMode ? '#121212' : '#f5f5f5',
        color: darkMode ? '#f5f5f5' : '#000',
        minHeight: '100vh'
      }}
    >
      <label style={{ display: 'block', marginBottom: '10px' }}>
        <input
          type="checkbox"
          checked={darkMode}
          onChange={(e) => setDarkMode(e.target.checked)}
          style={{ marginRight: '5px' }}
        />
        Dark Mode
      </label>

      <h1>{selectedSet.name}</h1>

      {/* Shuffle toggle */}
      <label style={{ display: 'block', marginBottom: '10px' }}>
        <input
          type="checkbox"
          checked={shuffle}
          onChange={(e) => setShuffle(e.target.checked)}
          style={{ marginRight: '5px' }}
        />
        Shuffle Mode
      </label>

      {/* Auto-flip */}
      <label style={{ display: 'block', marginBottom: '10px' }}>
        <input
          type="checkbox"
          checked={autoFlip}
          onChange={(e) => setAutoFlip(e.target.checked)}
          style={{ marginRight: '5px' }}
        />
        Auto-Flip Mode
      </label>

      {autoFlip && (
        <label style={{ display: 'block', marginBottom: '10px' }}>
          Flip Interval (ms):
          <input
            type="number"
            value={flipInterval}
            onChange={(e) => setFlipInterval(Number(e.target.value))}
            style={{ marginLeft: '5px', width: '80px' }}
          />
        </label>
      )}

      {/* Progress & keyboard hints */}
      <p style={{ fontSize: '0.9em', color: darkMode ? '#aaa' : '#555', marginBottom: '10px' }}>
        Card {index + 1} of {cards.length} | Keyboard: ← Previous | → Next | Space Flip | R Restart
      </p>

      {cards.length > 0 ? (
        <>
          <Flashcard
            front={cards[index].front}
            back={cards[index].back}
            flipped={flipped}
            onFlip={() => setFlipped(!flipped)}
            darkMode={darkMode}
          />

          <div style={{ marginTop: '10px' }}>
            <button
              onClick={prevCard}
              style={{
                marginRight: '10px',
                padding: '6px 12px',
                cursor: 'pointer',
                borderRadius: '6px',
                border: '1px solid #888',
                backgroundColor: darkMode ? '#333' : '#f0f0f0',
                color: darkMode ? '#f5f5f5' : '#000'
              }}
              disabled={shuffle}
            >
              Previous
            </button>

            <button
              onClick={nextCard}
              style={{
                marginRight: '10px',
                padding: '6px 12px',
                cursor: 'pointer',
                borderRadius: '6px',
                border: '1px solid #888',
                backgroundColor: darkMode ? '#333' : '#f0f0f0',
                color: darkMode ? '#f5f5f5' : '#000'
              }}
            >
              Next
            </button>

            <button
              onClick={restartSet}
              style={{
                padding: '6px 12px',
                cursor: 'pointer',
                borderRadius: '6px',
                border: '1px solid #888',
                backgroundColor: darkMode ? '#333' : '#f0f0f0',
                color: darkMode ? '#f5f5f5' : '#000'
              }}
            >
              {shuffle ? 'Restart Set (Random)' : 'Restart Set'}
            </button>
          </div>
        </>
      ) : (
        <p>No cards in this set.</p>
      )}

      <button
        onClick={() => setSelectedSet(null)}
        style={{
          marginTop: '20px',
          padding: '6px 12px',
          cursor: 'pointer',
          borderRadius: '6px',
          border: '1px solid #888',
          backgroundColor: darkMode ? '#333' : '#f0f0f0',
          color: darkMode ? '#f5f5f5' : '#000'
        }}
      >
        Back to Sets
      </button>
    </div>
  );
}
